<template>
  <model-form
    :model="registerStation"
    :modal-type="currentUser && currentUser.superUser ? 'maximized-modal' : 'modal'"
    @change="updateCashTrays"
  >
    <div class="column">
      <div class="row q-col-gutter-sm items-start">
        <q-input
          v-model="registerStation.name"
          class="col-6"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          :autofocus="!$q.platform.is.ios"
        />
        <q-select
          v-model="registerStation.registerType"
          class="col-6"
          label="Register Type"
          :options="$_.filter(registerTypes.data, { archivedAt: null })"
          option-label="name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
      </div>

      <div
        v-if="registerStation.registerType && registerStation.registerType.applicationType === APPLICATION_TYPES.REGISTER"
        class="row q-col-gutter-sm items-start"
      >
        <q-checkbox
          v-model="registerStation.displayTimeInHeader"
          class="col-6"
          label="Display Time in Header"
          bottom-slots
        />
      </div>
      <div
        v-if="registerStation.registerType && registerStation.registerType.applicationType === APPLICATION_TYPES.REGISTER"
        class="row q-col-gutter-sm items-start"
      >
        <q-checkbox
          v-model="registerStation.isOposCashdrawerEnabled"
          class="col-6"
          label="USB Cash Drawer Enabled"
          bottom-slots
        />
        <q-select
          v-if="registerStation.isOposCashdrawerEnabled"
          v-model="registerStation.oposCashDrawerType"
          class="col-6"
          label="Open Command Type"
          :option-label="$_.startCase"
          :options="CASH_DRAWER_TYPES.ALL"
          map-options
          bottom-slots
        />
        <q-input
          v-if="registerStation.isOposCashdrawerEnabled && registerStation.oposCashDrawerType === CASH_DRAWER_TYPES.SCRIPT"
          v-model="registerStation.cashdrawerCommandScript"
          class="col-12"
          type="textarea"
          label="Open Command Script"
          bottom-slots
        />
      </div>
      <div class="row q-col-gutter-sm items-start">
        <q-select
          v-if="currentSite.allowCashPayments && registerStation.registerType && registerStation.registerType.allowCashPayments"
          v-model="registerStation.automaticCashSessionsCashTrayId"
          class="col-6"
          label="Automated Cash Tray"
          :options="automaticCashSessionCashTrayOptions"
          option-label="name"
          option-value="id"
          map-options
          emit-value
          :disable="registerStation.registerType.cashCountType !== CASH_COUNT_TYPES.CASH_TRAY || !registerStation.registerType.enableAutomaticCashSessions"
          bottom-slots
          @update:model-value="updateAutomatedCashTray"
        />
        <div
          v-else
          class="col-6"
        />
        <q-select
          v-model="registerStation.hardware"
          class="col-6"
          label="Hardware"
          clearable
          :options="hardwares.data"
          option-label="name"
          :display-value="registerStation.hardware && registerStation.hardware.name"
          bottom-slots
        />
        <q-select
          v-model="registerStation.printerType"
          class="col-6"
          label="Windows Printer Type"
          :options="$mh.asOptions(PRINTER_TYPES.ALL)"
          map-options
          emit-value
          bottom-slots
        />
        <q-input
          v-model.number="registerStation.electronReceiptPaperWidth"
          class="col-6"
          label="Windows Machine Printer Width (px)"
          type="number"
          min="100"
          max="1000"
          bottom-slots
        />
        <q-select
          v-model="registerStation.barcodeType"
          class="col-6"
          label="Windows Barcode Type"
          :options="$mh.asOptions(BARCODE_TYPES.ALL)"
          map-options
          emit-value
          bottom-slots
        />
      </div>
      <payment-connector-configuration-table :register-station="registerStation" />
      <register-client-table
        v-if="currentUser && currentUser.superUser"
        class="q-mt-md"
        :register-station-id="registerStation.id"
      />
    </div>
  </model-form>
</template>

<script>
import { TYPES as CASH_COUNT_TYPES } from 'src/models/cash/CashCount'
import { BARCODE_TYPES, CASH_DRAWER_TYPES, POS_PAYMENT_TYPES, PRINTER_TYPES } from 'src/models/register/RegisterStation'
import { APPLICATION_TYPES } from 'src/models/register/RegisterType'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import PaymentConnectorConfigurationTable from 'src/modules/admin/payment-connector/PaymentConnectorConfigurationTable'
import RegisterClientTable from 'src/modules/admin/register-station/RegisterClientTable'

export default {
  name: 'RegisterStationForm',
  components: {
    ModelForm,
    RegisterClientTable,
    PaymentConnectorConfigurationTable
  },
  configuration: ['registerTypes', 'currentCircuit', 'currentSite'],
  context: ['currentUser'],
  data () {
    return {
      signageUrl: '',
      availableStripeReaders: [],
      createAttachedCashTray: false
    }
  },
  models: {
    registerStation: {
      registerType: 'default',
      hardware: 'default',
      paymentConnectorConfigurations: 'default'
    },
    hardwares: 'default',
    cashTrays: {
      registerStations: 'default'
    }
  },
  computed: {
    automaticCashSessionCashTrayOptions () {
      return _.concat({ name: 'New Cash Tray', id: null }, this.availableCashTrays)
    },
    availableCashTrays () {
      return _.filter(this.cashTrays.data, cashTray => {
        if (cashTray.archivedAt) return false
        const isRegisterStationRestricted = cashTray.enableRegisterStationRestriction && !_.includes(cashTray.registerStationIds, this.registerStation?.id)

        return !isRegisterStationRestricted
      })
    },
    stripeTerminalLocationId () {
      return this.registerStation?.registerType?.stripeTerminalLocationId
    }
  },
  watch: {
    'registerStation.id' () {
      if (this.registerStation?.registerType?.enableAutomaticCashSessions && this.registerStation?.automaticCashSessionsCashTrayId === null) {
        this.registerStation.action = 'create_attached_cash_tray'
      } else {
        this.registerStation.resetField('action')
      }
    }
  },
  async mounted () {
    if (this.stripeTerminalLocationId) {
      const result = await this.$payments.discoverTerminalReaders(this.stripeTerminalLocationId)
      if (!result?.readers) {
        return
      }
      _.each(result.readers, reader => this.availableStripeReaders.push({
        label: reader.label,
        value: reader.serial_number
      }))

      this.selectedReader = this.availableStripeReaders.find(reader => reader.value === this.registerStation.defaultStripeReaderSerialNumber)
    }
  },
  created () {
    _.extend(this, {
      APPLICATION_TYPES,
      BARCODE_TYPES,
      CASH_COUNT_TYPES,
      POS_PAYMENT_TYPES,
      PRINTER_TYPES,
      CASH_DRAWER_TYPES
    })
  },

  methods: {
    updateCashTrays () {
      if (this.registerStation.action === 'create_attached_cash_tray') {
        this.cashTrays.fetch(true)
        this.registerStation.resetField('action')
      }
    },
    updateAutomatedCashTray () {
      this.registerStation.resetField('action')
      if (this.registerStation?.registerType?.cashCountType !== CASH_COUNT_TYPES.CASH_TRAY) return

      if (this.registerStation?.registerType?.enableAutomaticCashSessions && this.registerStation?.automaticCashSessionsCashTrayId === null) {
        this.registerStation.action = 'create_attached_cash_tray'
      }
    }
  }
}

</script>
